import classNames from 'classnames'
import React from 'react'
import { UnifiedCard } from '../../../integrations/allkort/graphql/interface/Customer'
import { CloseIcon, PenIcon } from '../../svgs/ExpressonSVGs'
import { MastercardLogo, QStarLogo, VisaLogo } from '../../svgs/LogosSVGs'
import Button from '../Button'
import CardDisabledEditor from './CardDisabledEditor'
import CardNameEditor from './CardNameEditor'
import CardOrderReplacement from './CardOrderReplacement'
import CardPinEditor from './CardPinEditor'

type CardItemProps = {
	card: UnifiedCard
	isActive: boolean
	isCompany: boolean
	onCardClick: (cardId: string) => void
	onCardUpdate?: () => void
}

const getCardLabel = (card: UnifiedCard, isCompany: boolean): string => {
	if (card.cardType === 'bankcard') {
		return 'Bankkort'
	}
	if (card.type.shortName === 'P') {
		return 'Smartkort'
	}
	if (card.type.shortName === 'K') {
		return isCompany ? 'Företagskort' : 'Tankkort'
	}
	return card.type.name
}

const getCardLogo = (card: UnifiedCard): React.ReactNode => {
	if (card.cardType === 'bankcard') {
		if (card.issuer === 'Visa' || card.issuer === 'SwedishDebitVisa') {
			return <VisaLogo />
		}
		if (card.issuer === 'MasterCard') {
			return <MastercardLogo />
		}
		if (card.issuer === 'SwedishDebitMasterCard') {
			return <MastercardLogo />
		}
		return <span className="card-issuer-logo">{card.issuer}</span>
	}
	return <QStarLogo />
}

// Helper function to safely check if a card is disabled
const isCardDisabled = (card: UnifiedCard): boolean => {
	if (card.cardType === 'bankcard') {
		return false // Bank cards cannot be disabled
	}
	return card.limit?.disabled?.isDisabled || false
}

const CardItem: React.FC<CardItemProps> = ({ card, isActive, isCompany, onCardClick, onCardUpdate }) => {
	const handleClick = () => {
		// Bankcards should not be editable, prevent opening
		if (card.cardType === 'bankcard') {
			return
		}
		// Disabled cards should not be editable, prevent opening
		if (isCardDisabled(card)) {
			return
		}
		onCardClick(card.id)
	}

	const handleBackButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation()
		// Only allow closing if not a bankcard and not disabled
		if (card.cardType !== 'bankcard' && !isCardDisabled(card)) {
			onCardClick(card.id)
		}
	}

	const handleBackDivClick = (e: React.MouseEvent<HTMLDivElement>) => {
		e.stopPropagation()
	}

	const cardLabel = getCardLabel(card, isCompany)
	const cardLogo = getCardLogo(card)
	return (
		<div
			className={classNames(
				'card-item',
				{
					'card-item--active': isActive,
					'card-item--company': isCompany,
					'card-item--disabled': isCardDisabled(card),
					'card-item--smartcard': card.type.shortName === 'P',
					'card-item--creditcard': card.type.shortName === 'K',
					'card-item--bankcard': card.cardType === 'bankcard',
				}
			)}
		>
			{!isActive ? (
				<div
					className={classNames(
						"card-item-front",
						{
							'has-dark-bg': card.type.shortName === 'P' || card.cardType === 'bankcard',
						})}
					onClick={handleClick}
					tabIndex={0}
					role="button"
					aria-pressed={isActive}
					aria-label={`Card: ${card.type.name} - ${card.number || 'Bank Card'}`}
					onKeyDown={(e) => {
						if (e.key === 'Enter' || e.key === ' ') {
							handleClick()
						}
					}}
				>
					<div className="card-item-front__top">
						<div className="card-item-front__type">{cardLabel}</div>
						{cardLogo}
					</div>
					<div className="card-item-front__bottom">
						<div className='card-item-front__number'>
							{card.maskedPan || '••••'}
						</div>
						{card.cardType !== 'bankcard' && (
							<div className="card-item-front__reference">
								{card.name || '—'}
							</div>
						)}
					</div>
					{card.cardType !== 'bankcard' && (
						<div className="card-item-front__hover">
							{isCardDisabled(card) ?
								(
									<div>
										<div className="first-row-wrapper">
											<div className="strike-through-line"></div>
											<span className="card-item-front__disabled-label">Spärrat</span>
											<div className="strike-through-line"></div>
										</div>
										<span>{card.limit.disabled.at ? new Date(card.limit.disabled.at).toISOString().split('T')[0] : ''}</span>
									</div>

								) : (
									<>
										<PenIcon />
										Administrera kort
									</>
								)}
						</div>
					)}
				</div>
			) : (
				<div
					className={classNames(
						"card-item-back",
						{
							'has-dark-bg': card.type.shortName === 'P' || card.cardType === 'bankcard',
						})}
					onClick={handleBackDivClick}
				>
					<div className="card-item-back__info">
						<div className="card-item-back__top">
							<div>
								<p className="card-item-back__label">Kortnummer:</p>
								<div className='card-item-back__number'>
									{card.maskedPan || '••••'}
								</div>
							</div>
							<div>
								{cardLabel}
							</div>


						</div>
						<div className="card-item-back__content">
							<h4>Namn och pinkod</h4>
							<div className="handle-card-name">
								<div>
									<CardNameEditor
										cardId={card.id}
										isCreditCard={card.type.shortName === 'K' ? true : false}
										currentName={card.name || ''}
										maskedPan={card.maskedPan || '••••'}
										isCompany={isCompany}
										onUpdateSuccess={onCardUpdate}
									/>
								</div>
							</div>
							<div className="handle-card-pin">
								<div>
									<CardPinEditor
										cardId={card.id}
										isCreditCard={card.type.shortName === 'K' ? true : false}
										onUpdateSuccess={onCardUpdate}
									/>
								</div>
							</div>
							<div className="handle-card-other">
								<h6>Andra åtgärder</h6>

								<div className="handle-card-status">
									<div>
										<CardDisabledEditor
											cardId={card.id}
											cardType={card.type.shortName || 'K'}
											maskedPan={card.maskedPan || '••••'}
											cardName={card.name || ''}
											isCreditCard={card.type.shortName === 'K' ? true : false}
											onDisableSuccess={() => onCardClick(card.id)}
											isCompany={isCompany}
										/>
									</div>
								</div>

								<div className="handle-order-replacement">
									<CardOrderReplacement
										isCreditCard={card.type.shortName === 'K' ? true : false}
										isCompany={isCompany}
										hasSmartCard={card.type.shortName === 'P' ? true : false}
										hasCreditCard={card.type.shortName === 'K' ? true : false}
										card={card}
										onSuccess={onCardUpdate}
									/>
								</div>
							</div>

							<Button
								type="button"
								className="button-close"
								aria-label="Stäng kortdetaljer"
								onClick={handleBackButtonClick}
							>
								<CloseIcon />
							</Button>
						</div>

					</div>
				</div>
			)}
		</div>
	)
}

export default CardItem
